/**
 * Structured IPC error envelope shared between the main and renderer
 * processes. The wire format is a JSON string carried in `Error.message`,
 * prefixed with a sentinel so the renderer can detect it without ever
 * mistaking a regular error message for one.
 *
 * Why this shape rather than attaching properties to Error?
 *
 *   Electron's `ipcMain.handle` rejection path serialises only the error
 *   message + name back to the renderer — custom enumerable properties on
 *   the thrown Error are NOT preserved, and Error.cause is lost. Encoding
 *   the structured payload into the message is the only durable way to get
 *   `code`, `category`, `recovery`, and `retryable` across the boundary.
 *
 * The sentinel is intentionally distinctive (no leading/trailing whitespace,
 * unlikely to occur in a hand-written error string) and versioned so a
 * future change can co-exist with old payloads in flight.
 */
export const STRUCTURED_IPC_ERROR_SENTINEL = '[CRUCIBLE_ERR_V1]';
/**
 * Encode a SerialisedError as the wire-format string carried in
 * `Error.message` across the IPC boundary.
 */
export function encodeStructuredError(payload) {
    return STRUCTURED_IPC_ERROR_SENTINEL + JSON.stringify(payload);
}
/**
 * Detect whether a string looks like an encoded SerialisedError. Returns
 * the parsed payload, or `null` if the input is a normal error message.
 *
 * Robustness: a corrupt / partial payload returns `null` rather than
 * throwing — better to fall back to plain text than crash the renderer
 * because the encoder ever changes shape.
 */
export function tryDecodeStructuredError(message) {
    if (!message.startsWith(STRUCTURED_IPC_ERROR_SENTINEL))
        return null;
    const json = message.slice(STRUCTURED_IPC_ERROR_SENTINEL.length);
    try {
        const parsed = JSON.parse(json);
        if (!isSerialisedError(parsed))
            return null;
        return parsed;
    }
    catch {
        return null;
    }
}
function isSerialisedError(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    return (typeof v.name === 'string' &&
        typeof v.message === 'string' &&
        typeof v.category === 'string' &&
        typeof v.retryable === 'boolean');
}
/**
 * Renderer-side helper to reconstitute the structured payload from an
 * Error received via `window.crucible.invoke(...)`'s rejection path.
 *
 * Lives here (not in `main/error-utils.ts`) so the renderer can import it
 * without dragging the main-process module into its bundle.
 *
 * Returns:
 *   - the parsed SerialisedError if the input came from `flattenIpcError`,
 *   - a synthesised SerialisedError (`category: 'unknown'`, `retryable:
 *     false`) for ad-hoc errors without the sentinel, so the renderer
 *     can always pass one shape to `<CrucibleErrorState>`.
 *
 * Accepts `unknown` because IPC catches typically arrive as `unknown`.
 *
 * Robustness: Electron's IPC rejection wraps the thrown error message in
 * "Error invoking remote method 'channel': <Name>: <message>". We scan
 * for the sentinel anywhere in the string before falling back to the
 * raw text — that way the wrapped form decodes correctly too.
 */
export function unflattenIpcError(err) {
    if (err instanceof Error) {
        const sentinelIdx = err.message.indexOf(STRUCTURED_IPC_ERROR_SENTINEL);
        if (sentinelIdx >= 0) {
            const decoded = tryDecodeStructuredError(err.message.slice(sentinelIdx));
            if (decoded)
                return decoded;
        }
        return {
            name: err.name || 'Error',
            message: err.message,
            category: 'unknown',
            retryable: false,
        };
    }
    if (err === null || err === undefined) {
        return {
            name: 'Error',
            message: err === null ? 'null' : 'undefined',
            category: 'unknown',
            retryable: false,
        };
    }
    // Tier-5 follow-up: stream-chunk error payloads (`AgentStreamChunk.error`)
    // arrive here as raw strings rather than wrapped in an `Error`.
    // Detect the sentinel-prefixed envelope on string input too so the
    // renderer's chat-bubble path picks up structured `code` /
    // `category` / `recovery` instead of degrading to "unknown".
    if (typeof err === 'string') {
        const sentinelIdx = err.indexOf(STRUCTURED_IPC_ERROR_SENTINEL);
        if (sentinelIdx >= 0) {
            const decoded = tryDecodeStructuredError(err.slice(sentinelIdx));
            if (decoded)
                return decoded;
        }
        return {
            name: 'Error',
            message: err,
            category: 'unknown',
            retryable: false,
        };
    }
    return {
        name: 'Error',
        message: String(err),
        category: 'unknown',
        retryable: false,
    };
}
//# sourceMappingURL=error-types.js.map